import { memo, useEffect, useRef, useState } from "react";

// API servisi
import {
  checkUserEmailExistsAPI,
  checkUserNameExistsAPI,
  createUserAPI,
  getUserAPI,
  getUserRolesAPI,
  updateUserAPI,
} from "services/api/users/api";
import { getTenantAPI, getTenantRolesAPI } from "services/api/tenants/api";
import { UserEditFormValues } from "services/api/users/types";
import { getOfficesAPI } from "services/api/offices/api";
import { initialUserEditForm } from "services/api/users/consts";
import { initialDataWithPagination } from "services/api/consts";

// Validacija
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import {
  getEmailExistsError,
  getUserNameExistsError,
  USER_CREATE_FORM_SCHEMA,
  USER_EDIT_FORM_SCHEMA,
  USER_INFO_STEP_SCHEMA,
  USER_ACCOUNT_STEP_CREATE_SCHEMA,
  USER_ACCOUNT_STEP_EDIT_SCHEMA,
  USER_CONTACT_STEP_SCHEMA,
} from "./validation";

// Tipovi
import { UserEditProps } from "./types";
import { MultiDropdownOption } from "ui/components/Dropdown/MultiDropdown/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// UI komponente
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Loader } from "ui/components/Loader";
import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import invoiceAnimation from "ui/theme/animations/invoice.json";

// Koraci
import UserInfoStep from "./components/UserInfoStep/UserInfoStep";
import UserAccountStep from "./components/UserAccountStep/UserAccountStep";
import UserContactStep from "./components/UserContactStep/UserContactStep";
import UserSummaryStep from "./components/UserSummaryStep/UserSummaryStep";
import UserStepStatusBar from "./components/UserStepStatusBar/UserStepStatusBar";

// Korak "Korisnički podaci" — na njemu se unosi korisničko ime, pa se tu radi i provjera postojanja.
const ACCOUNT_STEP_INDEX = 1;

// Korak "Kontakt" — na njemu se unosi email, pa se tu radi i provjera postojanja.
const CONTACT_STEP_INDEX = 2;

const UserEditCard: React.FC<UserEditProps> = (props) => {
  const {
    userId,
    tenantId,
    officeId,
    show,
    isFullScreen = true,
    zPosition,
    onSuccess,
    onClose,
  } = props;

  const isEdit = !!userId;
  const isOfficeLocked = !!officeId;

  const [formValues, setFormValues] = useState<UserEditFormValues>(initialUserEditForm);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [stepCurrent, setStepCurrent] = useState(0);
  const [roleOptions, setRoleOptions] = useState<MultiDropdownOption[]>([]);
  const [officeOptions, setOfficeOptions] = useState<DropdownOption[]>([]);
  const [tenantName, setTenantName] = useState<string>("");
  const isProcessingRef = useRef(false);
  const initialEmailRef = useRef<string | null>(null);

  const baseTitle = isEdit ? "Ažuriranje korisnika" : "Unos korisnika";
  const title = tenantName ? `${baseTitle} [${tenantName}]` : baseTitle;

  useEffect(() => {
    if (!show) return;

    setStepCurrent(0);
    setErrors({});
    fetchUserRoles();
    fetchOffices();
    fetchTenantName();

    if (userId) {
      fetchUser(userId);
    } else {
      initialEmailRef.current = null;
      setFormValues({ ...initialUserEditForm, officeId: officeId ?? null });
    }
  }, [show, userId, officeId]);

  const fetchUser = async (id: string) => {
    setIsFetching(true);
    try {
      const result = await getUserAPI(id);
      initialEmailRef.current = result.email ?? null;
      setFormValues({
        ...result,
        userName: (result as any).username ?? result.userName,
        confirmPassword: "",
        roles: result.roles?.map((r: any) => r.id) ?? [],
      });
    } catch {
      // greška prikazana kroz snackbar
    }
    setIsFetching(false);
  };

  // Naziv tenanta za naslov, da se vidi kojem klijentu se korisnik dodaje.
  const fetchTenantName = async () => {
    if (!tenantId) {
      setTenantName("");
      return;
    }

    try {
      const tenant = await getTenantAPI(tenantId);
      setTenantName(
        tenant.companyName || [tenant.firstName, tenant.lastName].filter(Boolean).join(" ")
      );
    } catch {
      setTenantName("");
    }
  };

  const fetchUserRoles = async () => {
    try {
      const result = tenantId
        ? await getTenantRolesAPI(tenantId)
        : await getUserRolesAPI();
      setRoleOptions(
        result.map((role: any) => ({
          value: role.id.toString(),
          text: role.name,
        }))
      );
    } catch {
      setRoleOptions([]);
    }
  };

  const fetchOffices = async () => {
    try {
      const result = await getOfficesAPI(
        { ...initialDataWithPagination.pagination, pageSize: 100 },
        null,
        tenantId ?? undefined
      );
      setOfficeOptions(
        result.data.map((office) => ({
          value: office.id?.toString(),
          text: office.name,
        }))
      );
    } catch {
      setOfficeOptions([]);
    }
  };

  const stepSchemas = [
    USER_INFO_STEP_SCHEMA,
    isEdit ? USER_ACCOUNT_STEP_EDIT_SCHEMA : USER_ACCOUNT_STEP_CREATE_SCHEMA,
    USER_CONTACT_STEP_SCHEMA,
  ];

  // Koja polja pripadaju kojem koraku — za navigaciju na prvi korak s greškom.
  const stepFields: string[][] = [
    ["firstName", "lastName", "oib"],
    ["userName", "password", "confirmPassword", "roles"],
    ["email", "phoneNumber", "mobilePhoneNumber"],
  ];

  const findFirstErrorStep = (schema: any): number => {
    try {
      schema.validateSync(formValues, { abortEarly: false });
    } catch (err: any) {
      const paths: string[] = (err.inner ?? []).map((e: any) => e.path);
      const index = stepFields.findIndex((fields) =>
        fields.some((field) => paths.includes(field))
      );
      if (index >= 0) return index;
    }
    return 0;
  };

  // Provjerava postoji li već korisnik s unesenim emailom.
  // U edit modu provjera se preskače dok je email nepromijenjen.
  // Vraća poruku greške ako je email zauzet, inače null.
  const getEmailTakenError = async (): Promise<string | null> => {
    const email = formValues.email?.trim();
    if (!email) return null;

    const initialEmail = initialEmailRef.current?.trim();
    if (isEdit && initialEmail && email.toLowerCase() === initialEmail.toLowerCase()) {
      return null;
    }

    const result = await checkUserEmailExistsAPI(email);
    return result.exists ? getEmailExistsError(result.tenantName) : null;
  };

  const handleNextStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      const schema = stepSchemas[stepCurrent];
      if (schema) {
        const isValid = validateYup(formValues, schema, setErrors);
        if (!isValid) return;
      }

      if (stepCurrent === ACCOUNT_STEP_INDEX) {
        const userName = formValues.userName?.trim();
        if (userName) {
          const result = await checkUserNameExistsAPI(userName, userId);
          if (result.exists) {
            setErrors((prevErrors) => ({
              ...prevErrors,
              userName: getUserNameExistsError(result.tenantName),
            }));
            return;
          }
        }
      }

      if (stepCurrent === CONTACT_STEP_INDEX) {
        const emailTakenError = await getEmailTakenError();
        if (emailTakenError) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            email: emailTakenError,
          }));
          return;
        }
      }

      setStepCurrent((s) => s + 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleBackStep = () => {
    setStepCurrent((s) => s - 1);
  };

  const handleStepClick = (stepIndex: number) => {
    setStepCurrent(stepIndex);
  };

  const handleFinish = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    const schema = isEdit ? USER_EDIT_FORM_SCHEMA : USER_CREATE_FORM_SCHEMA;
    const isValid = validateYup(formValues, schema, setErrors);
    if (!isValid) {
      setStepCurrent(findFirstErrorStep(schema));
      isProcessingRef.current = false;
      return;
    }

    setIsLoading(true);
    try {
      if (userId) {
        await updateUserAPI(userId, formValues);
      } else {
        await createUserAPI(formValues, tenantId);
      }
      if (onSuccess) onSuccess();
    } catch {
      // greška prikazana kroz snackbar
    }
    setIsLoading(false);
    isProcessingRef.current = false;
  };

  const stepLabels = ["Osnovni podaci", "Korisnički podaci", "Kontakt", "Pregled"];

  const statusBar = (
    <UserStepStatusBar
      stepLabels={stepLabels}
      stepCurrent={stepCurrent}
      onStepClick={handleStepClick}
    />
  );

  const steps = [
    <WizardStep key="info" stepNumber={0} stepCurrent={stepCurrent}>
      {statusBar}
      <UserInfoStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
      />
    </WizardStep>,
    <WizardStep key="account" stepNumber={1} stepCurrent={stepCurrent}>
      {statusBar}
      <UserAccountStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        roleOptions={roleOptions}
        officeOptions={officeOptions}
        isEdit={isEdit}
        officeLocked={isOfficeLocked}
      />
    </WizardStep>,
    <WizardStep key="contact" stepNumber={2} stepCurrent={stepCurrent}>
      {statusBar}
      <UserContactStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
      />
    </WizardStep>,
    <WizardStep key="summary" stepNumber={3} stepCurrent={stepCurrent}>
      {statusBar}
      <UserSummaryStep
        formValues={formValues}
        roleOptions={roleOptions}
        officeOptions={officeOptions}
      />
    </WizardStep>,
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      size={isFullScreen ? "full" : "medium"}
      position={isFullScreen ? "side" : "centered"}
      zPosition={zPosition}
      intro={
        isFullScreen
          ? {
              content: (
                <ModalIntro
                  animation={invoiceAnimation}
                  title={
                    isEdit
                      ? "Ažurirajte podatke korisnika!"
                      : "Kreirajte novog korisnika u tren!"
                  }
                  description={
                    isEdit
                      ? "Uredite osnovne podatke, pristupne podatke i kontakt korisnika. Promjene su odmah dostupne u sustavu."
                      : "Unesite podatke o korisniku kroz nekoliko koraka. Nakon unosa, korisnik je odmah dostupan za prijavu u sustav."
                  }
                  show={true}
                />
              ),
            }
          : undefined
      }
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={title}
        onHide={onClose}
      />
      <ModalBody>
        {isFetching ? (
          <div className="relative min-h-[60vh] xl:min-h-[600px] h-[calc(100vh-160px)]">
            <Loader isLoading={true}>{null}</Loader>
          </div>
        ) : (
          <Wizard steps={steps} />
        )}
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(UserEditCard);
